import { Component, ChangeDetectionStrategy } from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import { FieldType, FieldTypeConfig, FormlyModule } from '@ngx-formly/core';
import { TranslatePipe } from '../../../../../shared/pipes/translate.pipe';

type DurationUnit = 'seconds' | 'minutes' | 'hours' | 'days';
type DurationDisplayFormat = 'short' | 'long';

@Component({
  selector: 'app-duration-field',
  imports: [ReactiveFormsModule, FormlyModule, TranslatePipe],
  templateUrl: './duration-field.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './duration-field.component.css',
})
export class DurationFieldComponent extends FieldType<FieldTypeConfig> {
  private readonly multipliers: Record<DurationUnit, number> = {
    seconds: 1,
    minutes: 60,
    hours: 3600,
    days: 86400,
  };

  get inputUnit(): DurationUnit {
    const unit = this.props['durationInputUnit'] as DurationUnit | undefined;
    return unit ?? 'minutes';
  }

  get displayFormat(): DurationDisplayFormat {
    const format = this.props['durationDisplayFormat'] as DurationDisplayFormat | undefined;
    return format ?? 'short';
  }

  get unitSuffix(): string {
    if (this.inputUnit === 'seconds') return 's';
    if (this.inputUnit === 'minutes') return 'min';
    if (this.inputUnit === 'hours') return 'h';
    return 'd';
  }

  get inputValue(): string {
    const seconds = this.parseSeconds(this.formControl.value);
    if (seconds === null) {
      return '';
    }
    const value = seconds / this.multipliers[this.inputUnit];
    return Number.isInteger(value) ? String(value) : value.toFixed(2);
  }

  onInput(event: Event): void {
    const rawValue = (event.target as HTMLInputElement).value;
    if (rawValue === '') {
      this.formControl.setValue(null);
      return;
    }

    const numericValue = Number(rawValue);
    if (!Number.isFinite(numericValue)) {
      this.formControl.setValue(null);
      return;
    }

    const seconds = numericValue * this.multipliers[this.inputUnit];
    this.formControl.setValue(seconds);
  }

  get formattedPreview(): string | null {
    const seconds = this.parseSeconds(this.formControl.value);
    if (seconds === null) {
      return null;
    }
    return this.formatDuration(seconds, this.displayFormat);
  }

  private parseSeconds(value: unknown): number | null {
    if (typeof value === 'number' && Number.isFinite(value)) {
      return value;
    }
    if (typeof value === 'string' && value.trim() !== '') {
      const parsed = Number(value);
      return Number.isFinite(parsed) ? parsed : null;
    }
    return null;
  }

  private formatDuration(totalSeconds: number, format: DurationDisplayFormat): string {
    const sign = totalSeconds < 0 ? '-' : '';
    const remainingStart = Math.abs(Math.trunc(totalSeconds));
    const days = Math.floor(remainingStart / 86400);
    let remaining = remainingStart % 86400;
    const hours = Math.floor(remaining / 3600);
    remaining %= 3600;
    const minutes = Math.floor(remaining / 60);
    const seconds = remaining % 60;

    if (format === 'short') {
      const parts: string[] = [];
      if (days) parts.push(`${days}d`);
      if (hours) parts.push(`${hours}h`);
      if (minutes) parts.push(`${minutes}m`);
      if (seconds || parts.length === 0) parts.push(`${seconds}s`);
      return `${sign}${parts.join(' ')}`;
    }

    const parts: string[] = [];
    if (days) parts.push(`${days} day${days === 1 ? '' : 's'}`);
    if (hours) parts.push(`${hours} hour${hours === 1 ? '' : 's'}`);
    if (minutes) parts.push(`${minutes} minute${minutes === 1 ? '' : 's'}`);
    if (seconds || parts.length === 0) parts.push(`${seconds} second${seconds === 1 ? '' : 's'}`);
    return `${sign}${parts.join(', ')}`;
  }
}
